import React, { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import {
  getMemberTrackerDetailApi,
  leaveMemberTrackerApi,
} from "src/DAL/tracker/tracker";
import {
  getCheckInSummary,
  getJourneySteps,
  getTrackerListMeta,
  isLeftTracker,
  isMyTracker,
  isRemovedByStaff,
  LEAVE_REASONS,
} from "./memberTrackersStorage";
import JourneyPasswordGate, {
  isJourneyPasswordRequired,
} from "./JourneyPasswordGate";
import MemberTrackerForm from "./MemberTrackerForm";
import {
  CheckInsTab,
  FeedbackCard,
  findLatestFeedback,
  JourneyBackLink,
  JourneyHeader,
  journeyPrimarySx,
  JourneyTabs,
  PerformanceCard,
  ReportsTab,
  ResultsCard,
  useJourneyResults,
} from "./MemberJourneyParts";
import { gr } from "src/design-system/tokens";
import GrButton from "src/design-system/components/GrButton";
import GrText from "src/design-system/components/GrText";

const MUTED = "#64748b";
const INK = "#0f172a";
const LEAVE_NOTE_MAX = 500;

const headerActionsSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: { xs: "stretch", sm: "flex-end" },
  gap: gr.space[1],
};
const stackSx = { display: "flex", flexDirection: "column", gap: gr.space[6] };

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "checkins", label: "Check-ins" },
  { value: "reports", label: "Reports" },
];

/** Confirm leaving a journey — reason required, note optional. */
const LeaveJourneyDialog = ({ open, submitting, error, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setNote("");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ fontWeight: 800, color: INK }}>
        Leave this journey?
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: MUTED, lineHeight: 1.5, mb: 2 }}>
          Your coach will be notified. Your history stays saved and you can
          rejoin later.
        </Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, mb: 0.5 }}>
          Why are you leaving?
        </Typography>
        <RadioGroup
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mb: 1.5 }}
        >
          {LEAVE_REASONS.map((item) => (
            <FormControlLabel
              key={item.value}
              value={item.value}
              control={<Radio size="small" disabled={submitting} />}
              label={item.label}
            />
          ))}
        </RadioGroup>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="Anything you'd like to add? (optional)"
          value={note}
          disabled={submitting}
          onChange={(e) => setNote(e.target.value.slice(0, LEAVE_NOTE_MAX))}
          inputProps={{ maxLength: LEAVE_NOTE_MAX }}
          helperText={`${note.length}/${LEAVE_NOTE_MAX}`}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ color: MUTED }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!reason || submitting}
          onClick={() => onConfirm({ reason, note: note.trim() })}
          startIcon={
            submitting ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          Leave journey
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function MemberTrackerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo } = useContentSetting();
  const [tracker, setTracker] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [progressReports, setProgressReports] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadNotFound, setLoadNotFound] = useState(false);
  const [submitNotice, setSubmitNotice] = useState("");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState("");
  const [lockedTracker, setLockedTracker] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const tabParam = String(searchParams.get("tab") || "");
  const tab = TABS.some((item) => item.value === tabParam) ? tabParam : "overview";
  const selectedStepId = searchParams.get("step") || null;
  // Tab / chosen check-in live in the URL (survive a reload).
  const goTo = (nextTab, stepId = null) => {
    const params = {};
    if (nextTab !== "overview") params.tab = nextTab;
    if (stepId) params.step = stepId;
    setSearchParams(params, { replace: true });
  };

  const openLeaveDialog = () => {
    setLeaveError("");
    setLeaveOpen(true);
  };

  const handleLeave = async ({ reason, note }) => {
    setLeaving(true);
    setLeaveError("");
    try {
      const res = await leaveMemberTrackerApi(id, { reason, note });
      if (res?.code !== 200) {
        setLeaveError(res?.message || "Could not leave this journey");
        return;
      }
      setSubmitNotice("");
      setLeaveOpen(false);
      enqueueSnackbar(res.message || "You have left this journey", {
        variant: "success",
      });
      // Reload: open check-ins are cancelled on leave.
      setReloadKey((key) => key + 1);
    } catch (_err) {
      setLeaveError("Could not leave this journey");
    } finally {
      setLeaving(false);
    }
  };

  useEffect(() => {
    if (location.state?.submitted) {
      setSubmitNotice("Submitted — your coach can see your answers.");
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadState((prev) => (prev === "ready" ? prev : "loading"));
      try {
        const res = await getMemberTrackerDetailApi(id);
        if (cancelled) return;
        if (res?.code === 200 && res.tracker) {
          setTracker({
            ...res.tracker,
            _memberSubmissions: res.submissions || [],
          });
          setSubmissions(
            (res.submissions || []).map((row) => ({
              ...row,
              id: String(row._id || row.id),
            }))
          );
          setProgressReports(
            (res.progressReports || []).map((row) => ({
              ...row,
              id: String(row._id || row.id),
            }))
          );
          setLoadState("ready");
        } else if (isJourneyPasswordRequired(res)) {
          setTracker(null);
          setSubmissions([]);
          setProgressReports([]);
          setLockedTracker(res.tracker || {});
          setLoadState("locked");
        } else {
          setTracker(null);
          setSubmissions([]);
          setProgressReports([]);
          setLoadNotFound(res?.code === 404);
          setLoadState("error");
        }
      } catch (_err) {
        if (cancelled) return;
        setTracker(null);
        setSubmissions([]);
        setProgressReports([]);
        setLoadNotFound(false);
        setLoadState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, userInfo?._id, reloadKey]);

  const steps = useMemo(
    () => (tracker ? getJourneySteps(tracker, submissions) : []),
    [tracker, submissions]
  );
  const summary = useMemo(
    () => (tracker ? getCheckInSummary(tracker, steps) : null),
    [tracker, steps]
  );
  const results = useJourneyResults(tracker, submissions);
  const feedback = useMemo(
    () => findLatestFeedback(submissions, steps),
    [submissions, steps]
  );
  // Check-ins tab lists every check-in of the journey (upcoming too).
  const listedCount = steps.length;

  if (loadState === "loading") {
    return (
      <div className="container-fluid">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </div>
    );
  }

  if (loadState === "locked") {
    return (
      <JourneyPasswordGate
        idOrSlug={id}
        tracker={lockedTracker}
        onUnlocked={() => {
          setLockedTracker(null);
          setReloadKey((key) => key + 1);
        }}
      />
    );
  }

  if (!tracker) {
    return (
      <div className="container-fluid">
        <button
          type="button"
          className="small-contained-button mb-3"
          onClick={() => navigate("/trackers")}
        >
          Back to Journeys
        </button>
        <Typography sx={{ fontWeight: 700, color: INK }}>
          {loadNotFound ? "Journey not found" : "Couldn't load this journey"}
        </Typography>
        <Typography sx={{ color: MUTED, mt: 0.5 }}>
          {loadNotFound
            ? "This journey may have been removed or you no longer have access."
            : "Something went wrong while loading it. Please try again."}
        </Typography>
        {!loadNotFound ? (
          <button
            type="button"
            className="small-contained-outlined-button mt-3"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  const isLeft = isLeftTracker(tracker);
  const removedByStaff = isRemovedByStaff(tracker);

  // Not joined yet: the start form right away (no empty Overview); once it is sent the page
  // reloads with the Overview / Check-ins / Reports tabs. Not open yet → the page as before.
  const joinMeta =
    !isLeft && !removedByStaff && !isMyTracker(tracker)
      ? getTrackerListMeta({ ...tracker, _memberSubmissions: submissions })
      : null;
  if (joinMeta?.canSubmit) {
    return (
      <div className="container-fluid">
        <JourneyBackLink onBack={() => navigate("/trackers")} />
        <Box sx={{ pb: gr.space[6], width: "100%" }}>
          <MemberTrackerForm
            tracker={tracker}
            onCancel={() => navigate("/trackers")}
            onSuccess={() => {
              setSubmitNotice("Submitted — your coach can see your answers.");
              setLoadState("loading");
              setReloadKey((key) => key + 1);
              window.scrollTo(0, 0);
            }}
          />
        </Box>
      </div>
    );
  }
  // Enrolled members (active or finished) can leave; already-left / not started cannot.
  const canLeave =
    !isLeft &&
    (tracker.membership === "active" || tracker.membership === "completed");
  // stepId: a missed check-in sent late; none = the open one.
  const startCheckIn = (stepId) => {
    setSubmitNotice("");
    navigate(
      `/trackers/${id}/submit${stepId ? `?step=${encodeURIComponent(stepId)}` : ""}`
    );
  };
  const lateTarget = !summary.open ? summary.lastOverdue : null;
  const canCheckIn = !isLeft && Boolean(summary.open || lateTarget);
  const tabCounts = { checkins: listedCount, reports: progressReports.length };

  return (
    <div className="container-fluid">
      {/* Only the rhythm beside the name; missed / due show on the Overview tiles and Check-ins tab. */}
      <JourneyHeader
        tracker={tracker}
        onBack={() => navigate("/trackers")}
        actions={
          <Box sx={headerActionsSx}>
            {isLeft ? (
              removedByStaff ? null : (
                <GrButton
                  sx={journeyPrimarySx}
                  onClick={() => navigate(`/trackers/${id}/submit`)}
                >
                  Rejoin journey
                </GrButton>
              )
            ) : canCheckIn ? (
              // Only when a check-in can be sent now (no greyed-out button while waiting).
              <GrButton
                sx={journeyPrimarySx}
                onClick={() => startCheckIn(lateTarget ? lateTarget.id : null)}
              >
                Check-in
              </GrButton>
            ) : null}
          </Box>
        }
      />
      {submitNotice || isLeft ? (
        <Box sx={{ mb: gr.space[4] }}>
          {submitNotice ? (
            <GrText
              variant="secondary"
              sx={{ color: gr.color.brandStrong, fontWeight: gr.font.weight.semibold }}
            >
              {submitNotice}
            </GrText>
          ) : null}
          {isLeft ? (
            <Alert severity="info" sx={{ mt: 1.5, maxWidth: 640 }}>
              {removedByStaff
                ? `Your ${tracker.leftBy === "admin" ? "gym" : "coach"} removed you from this journey${
                    tracker.leftAt
                      ? ` on ${moment(tracker.leftAt).format("D MMM YYYY")}`
                      : ""
                  }. Please contact your coach to rejoin.`
                : tracker.leftAt
                  ? `You left this journey on ${moment(tracker.leftAt).format(
                      "D MMM YYYY"
                    )}.`
                  : "You left this journey."}
            </Alert>
          ) : null}
        </Box>
      ) : null}

      <LeaveJourneyDialog
        open={leaveOpen}
        submitting={leaving}
        error={leaveError}
        onClose={() => setLeaveOpen(false)}
        onConfirm={handleLeave}
      />

      <JourneyTabs
        value={tab}
        onChange={(_e, value) => goTo(value)}
        tabs={TABS.map((item) => ({ ...item, count: tabCounts[item.value] }))}
        onLeave={canLeave ? openLeaveDialog : null}
      />

      {tab === "overview" ? (
        <Box sx={stackSx}>
          {/* No stats before the member has joined (or started) the journey. */}
          {isMyTracker(tracker) || isLeft ? <PerformanceCard tracker={tracker} summary={summary} /> : null}
          {results.length ? (
            <ResultsCard
              results={results}
              onAllReports={
                progressReports.length ? () => goTo("reports") : null
              }
            />
          ) : null}
          {feedback ? (
            <FeedbackCard
              feedback={feedback}
              onViewCheckIn={(stepId) => goTo("checkins", stepId)}
            />
          ) : null}
        </Box>
      ) : tab === "checkins" ? (
        <CheckInsTab
          steps={steps}
          summary={summary}
          selectedId={selectedStepId}
          onSelect={(stepId) => goTo("checkins", stepId)}
          onCheckIn={startCheckIn}
          canAct={!isLeft}
        />
      ) : (
        <ReportsTab reports={progressReports} />
      )}
    </div>
  );
}
